// modules/analytics/ui.ts — <app-panel-analisis>

import { bus }       from '../../core/event-bus';
import { getModule } from '../../core/registry';
import { useBus }    from '../../shared/hooks/use-bus';
import { formatCurrency } from '../../shared/utils/format';
import type { Period, FinancialSummary, ChartDataPoint } from '../../shared/types/global';

const PERIODS: { id: Period; label: string }[] = [
  { id: 'mes_actual', label: 'Este mes' },
  { id: 'semana',     label: '7 días'   },
  { id: 'mes',        label: '30 días'  },
  { id: 'trimestre',  label: '3 meses'  },
  { id: 'anio',       label: '1 año'    },
  { id: 'todo',       label: 'Todo'     },
];

const PALETTE = ['#14b8a6','#f59e0b','#ec4899','#a855f7','#22c55e','#06b6d4','#f97316','#84cc16'];

export class AppPanelAnalisis extends HTMLElement {
  private _period: Period = 'mes_actual';
  private _chartInstance: unknown = null;

  static define() { customElements.define('app-panel-analisis', AppPanelAnalisis); }

  connectedCallback() {
    this.render();
    this.loadData();

    useBus('transactions:loaded',  () => this.loadData(), this);
    useBus('transactions:saved',   () => this.loadData(), this);
    useBus('transactions:deleted', () => this.loadData(), this);
  }

  private render() {
    this.innerHTML = `
      <style>
        h2      { font-family:var(--font-display); font-size:var(--text-xl); margin-bottom:var(--space-4); }
        .period-bar { display:flex; gap: var(--space-2); flex-wrap:wrap; margin-bottom: var(--space-4); }
        .period-btn {
          background: var(--color-surface); border: 1px solid var(--color-border);
          border-radius: var(--radius-md); padding: var(--space-1) var(--space-3);
          font-family:var(--font-body); font-size:var(--text-xs); cursor:pointer; color:var(--color-text-muted);
          transition: all var(--duration-fast);
        }
        .period-btn.active { background:var(--color-primary); border-color:var(--color-primary); color:#fff; }
        .stats-grid  { display:grid; grid-template-columns:1fr 1fr; gap:var(--space-3); margin-bottom:var(--space-4); }
        .stat-card   { background:var(--color-surface); border:1px solid var(--color-border);
                       border-radius:var(--radius-lg); padding:var(--space-4); text-align:center; }
        .stat-card.wide { grid-column:1/-1; }
        .stat-value  { font-family:var(--font-display); font-size:var(--text-2xl); font-weight:var(--font-weight-bold);
                       font-variant-numeric: tabular-nums; }
        .stat-label  { font-size:var(--text-xs); color:var(--color-text-muted); margin-top:var(--space-1); }
        .chart-box   { background:var(--color-surface); border:1px solid var(--color-border);
                       border-radius:var(--radius-lg); padding:var(--space-4); margin-bottom:var(--space-3);
                       min-height:260px; display:flex; align-items:center; justify-content:center; }
        canvas       { max-height: 250px; }
      </style>
      <h2>Análisis</h2>
      <div class="period-bar">
        ${PERIODS.map(p => `
          <button class="period-btn ${p.id === this._period ? 'active' : ''}" data-period="${p.id}">${p.label}</button>
        `).join('')}
      </div>
      <div class="stats-grid" id="statsCards"></div>
      <div class="chart-box"><canvas id="pieChart"></canvas></div>
      <div class="chart-box"><canvas id="trendChart"></canvas></div>
    `;

    this.querySelectorAll<HTMLButtonElement>('.period-btn').forEach(btn => {
      btn.addEventListener('click', () => {
        this._period = btn.dataset.period as Period;
        this.querySelectorAll('.period-btn').forEach(b => b.classList.remove('active'));
        btn.classList.add('active');
        bus.emit('analytics:period-changed', { period: this._period });
        this.loadData();
      });
    });
  }

  private loadData() {
    const analytics = getModule<{
      getSummary: (p: Period) => FinancialSummary;
      getDistribution: (p: Period, t: 'Gasto' | 'Ingreso') => ChartDataPoint[];
    }>('analytics');
    if (!analytics) return;

    const summary = analytics.getSummary(this._period);
    const dist    = analytics.getDistribution(this._period, 'Gasto');

    this.renderStats(summary);
    this.renderCharts(dist, summary);
  }

  private renderStats(s: FinancialSummary) {
    const cards = this.querySelector('#statsCards')!;
    const pctColor = s.tasaAhorro >= 0 ? 'var(--color-income)' : 'var(--color-expense)';
    cards.innerHTML = `
      <div class="stat-card">
        <p class="stat-value" style="color:var(--color-income)">$${formatCurrency(s.ingresos)}</p>
        <p class="stat-label">Ingresos</p>
      </div>
      <div class="stat-card">
        <p class="stat-value" style="color:var(--color-expense)">$${formatCurrency(s.gastos)}</p>
        <p class="stat-label">Gastos</p>
      </div>
      <div class="stat-card">
        <p class="stat-value" style="color:${s.saldo >= 0 ? 'var(--color-income)' : 'var(--color-expense)'}">$${formatCurrency(s.saldo)}</p>
        <p class="stat-label">Balance</p>
      </div>
      <div class="stat-card">
        <p class="stat-value" style="color:${pctColor}">${s.tasaAhorro.toFixed(1)}%</p>
        <p class="stat-label">Tasa de ahorro</p>
      </div>
      ${s.topCategoria ? `
        <div class="stat-card wide">
          <p class="stat-label">Mayor gasto</p>
          <p class="stat-value" style="font-size:var(--text-lg)">${s.topCategoria.nombre}: $${formatCurrency(s.topCategoria.monto)}</p>
        </div>` : ''}
    `;
  }

  private async renderCharts(dist: ChartDataPoint[], summary: FinancialSummary) {
    // Cargar Chart.js de forma lazy
    const { Chart, registerables } = await import('chart.js');
    Chart.register(...registerables);

    // Pie chart
    const pieCanvas = this.querySelector<HTMLCanvasElement>('#pieChart')!;
    if ((pieCanvas as unknown as { _chartInstance?: unknown })._chartInstance) {
      (pieCanvas as unknown as { _chartInstance: { destroy: () => void } })._chartInstance.destroy();
    }
    if (dist.length > 0) {
      const pieChart = new Chart(pieCanvas, {
        type: 'doughnut',
        data: {
          labels:   dist.map(d => d.nombre),
          datasets: [{ data: dist.map(d => d.total), backgroundColor: PALETTE.slice(0, dist.length), borderWidth: 0 }],
        },
        options: {
          plugins: { legend: { position: 'bottom', labels: { color: 'var(--color-text-muted)', font: { size: 11 } } } },
          cutout: '50%',
        },
      });
      (pieCanvas as unknown as { _chartInstance: unknown })._chartInstance = pieChart;
    }

    // Trend chart
    const trendCanvas = this.querySelector<HTMLCanvasElement>('#trendChart')!;
    if ((trendCanvas as unknown as { _chartInstance?: unknown })._chartInstance) {
      (trendCanvas as unknown as { _chartInstance: { destroy: () => void } })._chartInstance.destroy();
    }
    if (summary.tendencia.length > 0) {
      const trendChart = new Chart(trendCanvas, {
        type: 'line',
        data: {
          labels:   summary.tendencia.map(t => t.mes),
          datasets: [
            { label: 'Ingresos', data: summary.tendencia.map(t => t.ingresos), borderColor: '#22c55e', tension: 0.4, pointRadius: 3 },
            { label: 'Gastos',   data: summary.tendencia.map(t => t.gastos),   borderColor: '#ef4444', tension: 0.4, pointRadius: 3 },
          ],
        },
        options: {
          plugins: { legend: { labels: { color: 'var(--color-text-muted)', font: { size: 11 } } } },
          scales: {
            x: { ticks: { color: 'var(--color-text-muted)' }, grid: { color: 'var(--color-border)' } },
            y: { ticks: { color: 'var(--color-text-muted)' }, grid: { color: 'var(--color-border)' } },
          },
        },
      });
      (trendCanvas as unknown as { _chartInstance: unknown })._chartInstance = trendChart;
    }
  }
}
